// Stage Mail-V2: 独立 Mail V2 API Client。
// V1 mail.ts とは 並行 に 存在 し、 一切 import し ない。
// backend /api/mail-v2/* に 対応。

import { apiFetch } from "./client";

// -------------------------------------------------------
// 共有 ヘルパー
// -------------------------------------------------------

function buildQs(q: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

function getCsrfToken(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return m ? decodeURIComponent(m[1]) : null;
}

async function uploadFile(
  path: string,
  file: File,
  extra?: Record<string, string>,
): Promise<unknown> {
  const fd = new FormData();
  fd.append("file", file);
  for (const [k, v] of Object.entries(extra ?? {})) {
    fd.append(k, v);
  }
  const headers: HeadersInit = {};
  const csrf = getCsrfToken();
  if (csrf) {
    (headers as Record<string, string>)["X-CSRF-Token"] = csrf;
  }
  const res = await fetch(`/api${path}`, {
    method: "POST",
    body: fd,
    credentials: "include",
    headers,
  });
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: unknown;
    error?: { code: string; message: string };
  };
  if (!res.ok || json.success === false) {
    throw new Error(json.error?.message ?? `HTTP ${res.status}`);
  }
  return json.data;
}

// -------------------------------------------------------
// API
// -------------------------------------------------------

export const mailV2Api = {
  // ----- accounts -----
  listAccounts: () =>
    apiFetch<MailAccountV2DTO[]>("/mail-v2/accounts"),
  getAccount: (id: string) =>
    apiFetch<MailAccountV2DTO>(`/mail-v2/accounts/${id}`),
  // Stage Mail-V2 (2026-07-22 Step 20): CRUD + test connections.
  createAccount: (body: Partial<MailAccountV2DTO> & {
    imap?: {
      host: string;
      port: number;
      security: string;
      username: string;
      password?: string | null;
    };
    smtp?: {
      host: string;
      port: number;
      security: string;
      username: string;
      password?: string | null;
    };
  }) =>
    apiFetch<MailAccountV2DTO>("/mail-v2/accounts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateAccount: (id: string, body: Partial<MailAccountV2DTO> & {
    imap?: { host?: string; port?: number; security?: string; username?: string; password?: string | null };
    smtp?: { host?: string; port?: number; security?: string; username?: string; password?: string | null };
  }) =>
    apiFetch<MailAccountV2DTO>(`/mail-v2/accounts/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteAccount: (id: string) =>
    apiFetch<{ status: string }>(`/mail-v2/accounts/${id}`, {
      method: "DELETE",
    }),
  testImap: (id: string) =>
    apiFetch<{ status: string; error?: string }>(
      `/mail-v2/accounts/${id}/test-imap`,
      { method: "POST" },
    ),
  testSmtp: (id: string) =>
    apiFetch<{ status: string; error?: string }>(
      `/mail-v2/accounts/${id}/test-smtp`,
      { method: "POST" },
    ),

  // ----- members -----
  listMembers: (accountId: string) =>
    apiFetch<MailMemberDTO[]>(`/mail-v2/accounts/${accountId}/members`),
  addMember: (accountId: string, body: { user_id: string; role: string }) =>
    apiFetch<MailMemberDTO>(`/mail-v2/accounts/${accountId}/members`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  removeMember: (accountId: string, userId: string) =>
    apiFetch<{ status: string }>(
      `/mail-v2/accounts/${accountId}/members/${userId}`,
      { method: "DELETE" },
    ),

  // ----- messages -----
  listMessages: (q: MailMessageListV2Query) =>
    apiFetch<MailMessageListV2Response>(
      `/mail-v2/messages${buildQs(q as Record<string, unknown>)}`,
    ),
  getMessage: (id: string) =>
    apiFetch<MailMessageV2DTO>(`/mail-v2/messages/${id}`),
  // 2026-08-24 mini-body recovery §2.7: 打开 meta-only 老邮件时按 UID 拉正文。
  // 响应 { ...MailMessageV2DTO, body_fetch: { status, repair_job_id?, purged? } }
  // body_fetch.status: 'ready' | 'repairing' | 'repair_required' | 'failed'
  fetchBody: (id: string) =>
    apiFetch<MailMessageV2DTO & {
      body_fetch: {
        status: "ready" | "repairing" | "repair_required" | "failed";
        repair_job_id?: string | null;
        purged?: boolean;
      };
    }>(`/mail-v2/messages/${id}/fetch-body`, {
      method: "POST",
    }),
  // Stage Mail-V2 Stage 3 (2026-07-22): Conversation View.
  // Returns full RFC-traced thread for a message:
  //   { root_id, current_id, items: MailMessageV2DTO[], total, truncated }
  getThread: (id: string) =>
    apiFetch<{
      root_id: string;
      current_id: string;
      items: MailMessageV2DTO[];
      total: number;
      truncated: boolean;
    }>(`/mail-v2/messages/${id}/thread`),
  patchMessage: (id: string, body: MailMessagePatchV2) =>
    apiFetch<MailMessageV2DTO>(`/mail-v2/messages/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  markRead: (id: string, isRead: boolean) =>
    apiFetch<MailMessageV2DTO>(`/mail-v2/messages/${id}/mark-read`, {
      method: "POST",
      body: JSON.stringify({ is_read: isRead }),
    }),
  toggleStar: (id: string) =>
    apiFetch<MailMessageV2DTO>(`/mail-v2/messages/${id}/toggle-star`, {
      method: "POST",
    }),
  unreadCount: (q?: { mail_account_id?: string }) =>
    apiFetch<{ unread: number }>(
      `/mail-v2/unread-count${q?.mail_account_id ? `?mail_account_id=${encodeURIComponent(q.mail_account_id)}` : ""}`,
    ),
  // Stage Mail-V2 (2026-07-22 Step 16): folder counts.
  folderCounts: (q?: { mail_account_id?: string }) =>
    apiFetch<Record<string, { total: number; unread: number }>>(
      `/mail-v2/folder-counts${q?.mail_account_id ? `?mail_account_id=${encodeURIComponent(q.mail_account_id)}` : ""}`,
    ),
  sharedMailMeta: (q?: { days?: number }) =>
    apiFetch<MailSharedMetaDTO>(
      `/mail-v2/shared-mail/meta${buildQs({ days: q?.days })}`,
    ),
  // Stage Mail-V2 (2026-07-22 Step 15): batch actions.
  batchActions: (
    messageIds: string[],
    action:
      | "mark_read"
      | "mark_unread"
      | "star"
      | "unstar"
      | "archive"
      | "trash"
      | "spam"
      | "unspam"
      | "restore_to_inbox"
      | "move"
      | "add_label"
      | "remove_label",
    payload?: { target_mailbox?: string; label_id?: string },
  ) =>
    apiFetch<{
      updated: number;
      skipped: number;
      errors: { message_id: string; error_code: string; message: string }[];
      total: number;
    }>("/mail-v2/messages/batch-actions", {
      method: "POST",
      body: JSON.stringify({
        message_ids: messageIds,
        action,
        payload: payload ?? {},
      }),
    }),

  // ----- drafts -----
  listDrafts: () => apiFetch<MailDraftDTO[]>("/mail-v2/drafts"),
  // Stage Mail-V2 Stage 4 (2026-07-22): Drafts folder list 走 mail_drafts 表,
  // 不 走 mail_messages.mailbox_name='DRAFTS'(后者 永远 NULL)。
  // 支持 mail_account_id / smtp_status (comma) / parent_message_id 过滤。
  listDraftsByFolder: (q?: {
    mail_account_id?: string;
    smtp_status?: string; // 逗号分隔,默认 "draft,sending"
    parent_message_id?: string;
  }) => {
    const qs = q
      ? `?${new URLSearchParams(
          Object.entries(q).filter(([, v]) => v !== undefined && v !== "") as [
            string,
            string,
          ][],
        ).toString()}`
      : "";
    return apiFetch<MailDraftDTO[]>(`/mail-v2/drafts/list-by-folder${qs}`);
  },
  getDraft: (id: string) =>
    apiFetch<MailDraftDTO>(`/mail-v2/drafts/${id}`),
  upsertDraft: (body: MailDraftUpsertV2) =>
    apiFetch<MailDraftDTO>("/mail-v2/drafts", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  sendDraft: (id: string, idempotencyKey: string) =>
    apiFetch<MailDraftDTO>(`/mail-v2/drafts/${id}/send`, {
      method: "POST",
      body: JSON.stringify({ idempotency_key: idempotencyKey }),
    }),
  deleteDraft: (id: string) =>
    apiFetch<{ status: string }>(`/mail-v2/drafts/${id}`, {
      method: "DELETE",
    }),

  // ----- AI generation (2026-07-24 重构) -----
  // 非流式: 一次 POST + 同步等 AIEngineClient.generate() + 一次 JSON 响应。
  // 无 SSE, 无 streaming, 无 job_id, 无 resume, 无 cancel, 无 DB 持久化。
  // 生成完成数据丢掉。生成期间 UI 显示 loading 转圈。
  // 2026-07-24 第 2 轮: URL 改名 ai-reply-stream → ai-reply。
  // POST /api/mail-v2/messages/<id>/ai-reply
  // 成功: { success: true, data: { text, status, usage } }
  // 失败: { success: false, error: { code, message } }
  generateAiReply: async (
    messageId: string,
    body: { instruction?: string; tone?: string },
  ): Promise<{ text: string; status: string; usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number } }> => {
    const csrf = (() => {
      if (typeof document === "undefined") return "";
      const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
      return m && m[1] ? decodeURIComponent(m[1]) : "";
    })();
    let res: Response;
    try {
      res = await fetch(
        `/api/mail-v2/messages/${messageId}/ai-reply`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify(body),
        },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "unknown fetch error";
      throw new Error(`NETWORK_ERROR: ${msg}`);
    }
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      let message = `HTTP ${res.status}`;
      try {
        const j = (await res.json()) as {
          success?: boolean;
          error?: { code?: string; message?: string };
        };
        if (j && j.error) {
          code = j.error.code ?? code;
          message = j.error.message ?? message;
        }
      } catch {
        // non-JSON fallback
      }
      throw new Error(`${code}: ${message}`);
    }
    const j = (await res.json()) as {
      success?: boolean;
      data?: {
        text?: string;
        status?: string;
        usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
      };
    };
    const data = j.data || {};
    return {
      text: typeof data.text === "string" ? data.text : "",
      status: typeof data.status === "string" ? data.status : "succeeded",
      usage: data.usage || { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
    };
  },

  // ----- AI compose (new mail, 2026-07-24) -----
  // 与 generateAiReply 同形态,但 endpoint 不同(无 message_id URL 段)。
  // instruction 可空:后端空时用默认 user prompt "新規のビジネスメール本文を作成してください。"
  // POST /api/mail-v2/ai-compose
  generateAiCompose: async (
    body: { instruction: string; tone?: string },
  ): Promise<{ text: string; status: string; usage: { prompt_tokens: number; completion_tokens: number; total_tokens: number } }> => {
    const csrf = (() => {
      if (typeof document === "undefined") return "";
      const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
      return m && m[1] ? decodeURIComponent(m[1]) : "";
    })();
    let res: Response;
    try {
      res = await fetch(
        `/api/mail-v2/ai-compose`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRF-Token": csrf } : {}),
          },
          body: JSON.stringify(body),
        },
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "unknown fetch error";
      throw new Error(`NETWORK_ERROR: ${msg}`);
    }
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      let message = `HTTP ${res.status}`;
      try {
        const j = (await res.json()) as {
          success?: boolean;
          error?: { code?: string; message?: string };
        };
        if (j && j.error) {
          code = j.error.code ?? code;
          message = j.error.message ?? message;
        }
      } catch {
        // non-JSON fallback
      }
      throw new Error(`${code}: ${message}`);
    }
    const j = (await res.json()) as {
      success?: boolean;
      data?: {
        text?: string;
        status?: string;
        usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
      };
    };
    const data = j.data || {};
    return {
      text: typeof data.text === "string" ? data.text : "",
      status: typeof data.status === "string" ? data.status : "succeeded",
      usage: data.usage || { prompt_tokens: 0, completion_tokens: 0, total_tokens: 0 },
    };
  },

  // ----- attachments -----
  uploadAttachment: (file: File, opts?: { mailDraftId?: string }) =>
    uploadFile("/mail-v2/attachments/upload", file, {
      mail_draft_id: opts?.mailDraftId ?? "",
    }) as Promise<MailAttachmentDTO>,
  getAttachmentDownloadUrl: (id: string, expiresIn = 604800) =>
    apiFetch<{
      attachment_id: string;
      url: string;
      expires_in: number;
      filename: string;
      content_type: string;
      size_bytes: number;
    }>(
      `/mail-v2/attachments/${id}/download-url?expires_in=${expiresIn}`,
    ),
  attachToDraft: (attachmentId: string, mailDraftId: string) =>
    apiFetch<MailAttachmentDTO>(
      `/mail-v2/attachments/${attachmentId}/attach-to-draft`,
      { method: "POST", body: JSON.stringify({ mail_draft_id: mailDraftId }) },
    ),
  // Stage Mail-V2 (2026-07-22 Step 14): list / delete — composer refresh recovery.
  listAttachmentsForDraft: (draftId: string) =>
    apiFetch<MailAttachmentDTO[]>(
      `/mail-v2/drafts/${draftId}/attachments`,
    ),
  // 2026-08-25: 详情页 "这封邮件带了什么附件" — 后端
  // GET /api/mail-v2/messages/<id>/attachments 走 visibility check (viewer 可读),
  // 返回 DTO list 格式与 listAttachmentsForDraft 保持一致,前端 MailAttachmentDTO 直接复用。
  listMessageAttachments: (messageId: string) =>
    apiFetch<MailAttachmentDTO[]>(
      `/mail-v2/messages/${messageId}/attachments`,
    ),
  deleteAttachment: (id: string) =>
    apiFetch<{ status: string }>(`/mail-v2/attachments/${id}`, {
      method: "DELETE",
    }),

  // ----- labels -----
  listLabels: () => apiFetch<MailLabelDTO[]>("/mail-v2/labels"),
  createLabel: (body: { name: string; color?: string }) =>
    apiFetch<MailLabelDTO>("/mail-v2/labels", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deleteLabel: (id: string) =>
    apiFetch<{ status: string }>(`/mail-v2/labels/${id}`, {
      method: "DELETE",
    }),

  // ----- Stage Mail-V2 (2026-07-22):真实 IMAP action -----
  // archive / trash / spam(true|false) / move(target_mailbox)。
  // 后端 会 走真 UID MOVE / fallback UID COPY + STORE \\Deleted + EXPUNGE。
  // 错误时 返 501 MAIL_V2_NO_SPECIAL_USE 或 502 MAIL_V2_IMAP_*;前端 不许 only toast。
  messageAction: (
    messageId: string,
    body:
      | { action: "archive" }
      | { action: "trash" }
      | { action: "spam" }
      | { action: "unspam" }
      | { action: "restore_to_inbox" }
      | { action: "move"; target_mailbox: string },
  ) =>
    apiFetch<MailMessageV2DTO>(`/mail-v2/messages/${messageId}/action`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // 2026-07-24:Trash「完全削除」(Delete Forever)— 不可逆。
  // レスポンスに already_deleted(サーバには無くて DB 残留してた → DB クリーン済)を追加。
  permanentDelete: (messageIds: string[]) =>
    apiFetch<{
      deleted: string[];
      already_deleted?: string[];
      failed: { id: string; reason: string; message?: string }[];
      total: number;
    }>(
      "/mail-v2/messages/permanent",
      {
        method: "DELETE",
        body: JSON.stringify({ message_ids: messageIds }),
      },
    ),
};

// -------------------------------------------------------
// Types (raw snake_case from backend)
// -------------------------------------------------------

export type MailAccountV2DTO = {
  id: string;
  tenant_id: string;
  name: string;
  email_address: string;
  imap_host: string;
  imap_port: number;
  imap_security: string;
  imap_username: string;
  has_imap_password: boolean;
  imap_password_hint: string;
  smtp_host: string;
  smtp_port: number;
  smtp_security: string;
  smtp_username: string;
  has_smtp_password: boolean;
  smtp_password_hint: string;
  is_active: boolean;
  account_type: "personal" | "shared" | "other";
  owner_user_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
  /** Mail-V2.5 (2026-08-19): 自動作成された initial sync job の id。
   *  POST /api/mail-v2/accounts の response body に含まれる。
   *  null = 自動起動失敗 or job 既に取られた。 */
  sync_job_id?: string | null;
  /** Mail V2.5 (2026-08-19): 同期状態。'error' のとき frontend で 修復 ボタンを
   *  enabled にし、リスト / 詳細 / 書 き込み を disable する。 */
  last_sync_status?: "ok" | "error";
  last_sync_error?: string | null;
  last_sync_error_at?: string | null;
};

export type MailMemberDTO = {
  mail_account_id: string;
  user_id: string;
  role: "viewer" | "editor" | "admin";
  created_at: string;
};

export type MailSharedMetaDTO = {
  period: {
    days: number;
    start: string;
    end: string;
  };
  shared_account_count: number;
  shared_mail_count: number;
  unread_count: number;
  unhandled_count: number;
  handling_count: number;
  completed_count: number;
  daily: {
    date: string;
    total: number;
    unread: number;
    unhandled: number;
  }[];
  accounts: {
    id: string;
    name: string;
    email_address: string;
    total: number;
    unread: number;
    unhandled: number;
  }[];
  signals: {
    key: string;
    severity: "high" | "medium" | "low";
    value: number;
    title: string;
  }[];
};

export type MailMessageV2DTO = {
  id: string;
  tenant_id: string;
  mail_account_id: string;
  direction: "inbound" | "outbound";
  from_email: string;
  from_name: string | null;
  to_addresses: string | null;
  cc_addresses: string | null;
  reply_to_addresses: string | null;
  reply_to: string | null;
  reply_all_to: string | null;
  reply_all_cc: string | null;
  subject: string | null;
  preview: string | null;
  body_text: string | null;
  body_html_sanitized: string | null;
  received_at: string | null;
  created_at: string;
  updated_at: string | null;
  is_read: boolean;
  is_important: boolean;
  is_starred: boolean;
  status: "unhandled" | "handling" | "completed";
  smtp_status: "pending" | "sent" | "failed" | null;
  mailbox_name: string | null;
  parent_message_id: string | null;
  // Stage Mail-V2 Stage 3 (2026-07-22): RFC threading metadata.
  message_id: string | null;
  references_header: string | null;
  has_attachments: boolean;
  // Stage Mail-V2 Stage 4 (2026-07-22): sub-2 rebind. mail_attachments 表里
  // mail_message_id == this.id 的 attachment id 列表。Sent folder 显示 chip 用。
  attachment_ids: string[] | null;
};

export type MailMessageListV2Query = {
  mail_account_id?: string;
  mailbox?: string;
  status?: string;
  is_read?: boolean;
  is_starred?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
  label_id?: string;
};

export type MailMessageListV2Response = {
  items: MailMessageV2DTO[];
  total: number;
  page: number;
  page_size: number;
};

export type MailMessagePatchV2 = {
  status?: "unhandled" | "handling" | "completed";
  is_read?: boolean;
  is_important?: boolean;
};

export type MailDraftDTO = {
  id: string;
  tenant_id: string;
  user_id: string;
  mail_account_id: string;
  parent_message_id: string | null;
  to_addresses: string | null;
  cc_addresses: string | null;
  bcc_addresses: string | null;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  ai_generated_body: string | null;
  smtp_status: "draft" | "sending" | "sent" | "failed";
  smtp_error: string | null;
  idempotency_key: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  // Stage Mail-V2 Stage 4 (2026-07-22): Drafts folder list 包含 parent 信息,
  // 让前端可以渲染 "Re: 〇〇" 标题。仅 list_drafts_for_folder 返回,
  // 旧 list_drafts() 不会带。
  parent_subject?: string | null;
  parent_sender_email?: string | null;
};

export type MailDraftUpsertV2 = {
  id?: string;
  idempotency_key?: string;
  mail_account_id?: string;
  parent_message_id?: string | null;
  to_addresses?: string;
  cc_addresses?: string;
  bcc_addresses?: string;
  subject?: string;
  body_text?: string;
  body_html?: string;
  ai_generated_body?: string;
};

export type MailAiJobDTO = {
  job_id: string;
  status: string;
  task_type: string;
  tenant_id: string;
  user_id: string;
  mail_message_id: string | null;
  draft_text: string | null;
  error_code: string | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  expires_at: string;
};

export type MailAttachmentDTO = {
  id: string;
  tenant_id: string;
  mail_message_id: string | null;
  mail_draft_id: string | null;
  direction: "inbound" | "outbound";
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  storage_backend: "gcs" | "local_fs";
  content_id: string | null;
  is_inline: boolean;
  created_by: string | null;
  created_at: string;
};

export type MailLabelDTO = {
  id: string;
  tenant_id: string;
  name: string;
  color: string | null;
  created_by: string | null;
  created_at: string;
};

// ============================================================
// Mail-V2.5 Sync Job DTOs (2026-08-19)
// ============================================================

export type MailSyncJobProgressDTO = {
  id: string;
  job_id: string;
  mail_account_id: string;
  mailbox_name: string;
  target_count: number;
  processed_count: number;
  failed_count: number;
  last_processed_uid: number | null;
  status: "pending" | "running" | "completed" | "failed" | "skipped";
  error_message: string | null;
};

export type MailSyncJobDTO = {
  id: string;
  tenant_id: string;
  mail_account_id: string;
  /** Mail V2.5 (2026-08-19): 'initial' renamed to 'initial_sync'. */
  job_type: "initial_sync" | "incremental" | "backfill" | "repair";
  initial_window: number | null;
  total_target_count: number;
  processed_count: number;
  failed_count: number;
  priority: number;
  status: "pending" | "running" | "completed" | "failed" | "stale";
  started_at: string | null;
  finished_at: string | null;
  last_heartbeat_at: string | null;
  error_message: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  progress?: MailSyncJobProgressDTO[];
};

/** Mail V2.5 (2026-08-20): response shape after mutex + account-level state.
 *  Locked plan §16.9 — status field is one of 'created' | 'merged' | 'existing'. */
export type MailSyncJobCreateResult = {
  status: "created" | "merged" | "existing";
  job_id?: string;
  existing_job_id?: string;
  /** Convenience alias: the sync job id to follow (== job_id on created,
   *  == existing_job_id on existing/merged). Frontend reads this to set activeSyncJobId. */
  sync_job_id?: string;
};

// ============================================================
// mailV2Api.syncJobs namespace (2026-08-19)
// ============================================================

export const syncJobs = {
  /** POST /api/mail-v2/sync-jobs/initial */
  initial: (mail_account_id: string, initial_window: number = 200) =>
    apiFetch<MailSyncJobCreateResult>(`/mail-v2/sync-jobs/initial`, {
      method: "POST",
      body: JSON.stringify({ mail_account_id, initial_window }),
    }),

  /** POST /api/mail-v2/sync-jobs/refresh — manual refresh / spinner button */
  refresh: (mail_account_id: string) =>
    apiFetch<MailSyncJobCreateResult>(`/mail-v2/sync-jobs/refresh`, {
      method: "POST",
      body: JSON.stringify({ mail_account_id }),
    }),

  /** POST /api/mail-v2/sync-jobs/backfill — extend initial window */
  backfill: (mail_account_id: string, new_initial_window: number) =>
    apiFetch<MailSyncJobCreateResult>(`/mail-v2/sync-jobs/backfill`, {
      method: "POST",
      body: JSON.stringify({ mail_account_id, new_initial_window }),
    }),

  /** POST /api/mail-v2/sync-jobs/repair */
  repair: (mail_account_id: string) =>
    apiFetch<MailSyncJobCreateResult>(`/mail-v2/sync-jobs/repair`, {
      method: "POST",
      body: JSON.stringify({ mail_account_id }),
    }),

  /** GET /api/mail-v2/sync-jobs/<id> — overlay polling */
  get: (job_id: string) =>
    apiFetch<MailSyncJobDTO>(`/mail-v2/sync-jobs/${job_id}`),

  /** GET /api/mail-v2/sync-jobs — list active jobs (locked plan §16.9.2 / §C).
   *  Accepts mail_account_id + status + target_mailbox filters.
   *  status='active' (default) maps to pending|running|stale on the backend.
   *  target_mailbox 用精确字符串匹配(不是 LIKE),用于 mailbox rebuild repair 探测。*/
  listActive: (params: { mail_account_id?: string; status?: string; target_mailbox?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.mail_account_id) qs.set("mail_account_id", params.mail_account_id);
    if (params.status) qs.set("status", params.status);
    if (params.target_mailbox) qs.set("target_mailbox", params.target_mailbox);
    const q = qs.toString();
    return apiFetch<{ jobs: MailSyncJobDTO[] }>(
      `/mail-v2/sync-jobs${q ? "?" + q : ""}`,
    );
  },
  cancel: (job_id: string) =>
    apiFetch<{ status: "cancelled"; job_id: string }>(`/mail-v2/sync-jobs/${job_id}/cancel`, {
      method: "POST",
    }),
};
